// Replay (spec 10.1, 10.2; plan step 3.3): from a session's segments to the wireframe at any
// moment. Pure: no React, no fetch. A segment is customer-controlled data, so it is checked
// against the spec's shape before anything uses it (a bad segment becomes a gap, never a crash),
// colors are reduced to numbers, and node counts and text lengths are capped so a hostile
// segment cannot freeze the tab.
//
// Frames from every segment are put in seq order. A keyframe holds the viewport and every node;
// a delta updates (reparenting included), then removes, then adds nodes by id. The state at a
// moment is the last keyframe at or before it with every delta after it applied, as long as no
// seq is missing in between: a missing seq, a segment that never arrived or did not parse, and
// deltas before any keyframe are gaps, until the next keyframe.
//
// The work is bounded whatever the segments hold: a timeline keeps at most LIMITS.frames frames
// and LIMITS.ops node operations (the rest is a gap); a removal costs the removed subtree only
// (a parent-to-children index), an unknown id nothing; and the state is built incrementally,
// from the last moment shown or a checkpoint every LIMITS.checkpointOps operations, and
// memoized per frame, so playing and scrubbing never replay a long run from its keyframe twice.

export const LIMITS = {
  /** Nodes in one frame's list, and in the state at any moment. */
  nodes: 3_000,
  /** Frames in one segment (about 10 seconds; at most 2 a second is the spec's pace). */
  framesPerSegment: 2_000,
  /** Characters of one text node that are drawn. */
  textChars: 500,
  /** Touches in one frame. */
  touches: 500,
  /** Ancestors walked to place a node. */
  depth: 64,
  /** Frames in a timeline (about 100 segments at the most); later ones are a gap. */
  frames: 50_000,
  /** Node operations in a timeline (keyframe nodes, adds, updates, removals, touches). */
  ops: 1_000_000,
  /** Operations between two saved states: the most a seek replays once they exist. */
  checkpointOps: 20_000,
  /** Frames looked back for the touches of the last half second. */
  touchFrames: 64,
} as const

export const KINDS = [
  'container',
  'text',
  'input',
  'button',
  'image',
  'switch',
  'slider',
  'scroll',
  'keyboard',
  'webview',
  'map',
  'video',
  'hidden',
] as const
export type Kind = (typeof KINDS)[number]

/** A color as numbers: never a string from the segment. */
export interface Rgba {
  r: number
  g: number
  b: number
  /** 0 to 1. */
  a: number
}

export interface Style {
  background?: Rgba
  borderColor?: Rgba
  borderWidth?: number
  cornerRadius?: number
}

export interface Text {
  value: string
  masked: boolean
  size?: number
  weight?: number
  color?: Rgba
  align?: 'start' | 'center' | 'end' | 'justified'
  lines?: number
}

export interface State {
  selected?: boolean
  enabled?: boolean
  on?: boolean
  value?: number
}

export interface Node {
  id: number
  parent?: number
  kind: Kind
  rect: [number, number, number, number]
  scroll?: [number, number]
  clip?: boolean
  alpha?: number
  style?: Style
  text?: Text
  state?: State
  hiddenReason?: 'setting' | 'unreadable' | 'limit'
}

export type NodeUpdate = Partial<Omit<Node, 'id'>> & { id: number }

export interface Touch {
  /** Milliseconds since the Unix epoch. */
  at: number
  x: number
  y: number
  phase: 'down' | 'move' | 'up' | 'cancel'
}

export interface Viewport {
  width: number
  height: number
  scale: number
  safeArea?: [number, number, number, number]
  orientation?: 'portrait' | 'landscape'
}

export interface Frame {
  seq: number
  /** Milliseconds since the Unix epoch (from the frame's Unix nanoseconds). */
  at: number
  type: 'keyframe' | 'delta'
  viewport?: Viewport
  nodes?: Node[]
  add?: Node[]
  update?: NodeUpdate[]
  remove?: number[]
  touches?: Touch[]
}

export type Parsed = { ok: true; frames: Frame[] } | { ok: false; reason: string }

// --- Shape checks (schemas/v0.1/replay-segment.schema.json). Unknown fields are ignored, as
// the spec tells players; a known field of the wrong shape fails the whole segment.

class Invalid extends Error {}
const fail = (reason: string): never => {
  throw new Invalid(reason)
}
const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)
const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const isIndex = (v: unknown): v is number => Number.isSafeInteger(v) && (v as number) >= 0

function num(v: unknown, what: string, min = -Infinity, max = Infinity): number {
  if (!isNumber(v) || v < min || v > max) fail(`${what} is not a number in range`)
  return v as number
}
function optional<T>(
  o: Record<string, unknown>,
  key: string,
  read: (v: unknown) => T,
): T | undefined {
  return o[key] === undefined ? undefined : read(o[key])
}
function oneOf<T extends string>(v: unknown, allowed: readonly T[], what: string): T {
  if (typeof v !== 'string' || !(allowed as readonly string[]).includes(v))
    fail(`${what} is unknown`)
  return v as T
}
const bool = (what: string) => (v: unknown) =>
  typeof v === 'boolean' ? v : fail(`${what} is not a boolean`)

/** Unix nanoseconds as a decimal string, to milliseconds. */
export function nanosToMs(v: unknown): number {
  if (typeof v !== 'string' || !/^[0-9]{1,20}$/.test(v)) fail('a time is not Unix nanoseconds')
  const ns = BigInt(v as string)
  // Whole milliseconds, like every other time on the page (lib/session parseTime): a segment's
  // start from the read API is cut to the millisecond, and a frame a fraction later would leave
  // the player's first moment, the segment's start, before the first frame.
  return Number(ns / 1_000_000n)
}

/** #RRGGBBAA, the spec's only color form, to numbers. */
export function color(v: unknown): Rgba {
  if (typeof v !== 'string' || !/^#[0-9a-fA-F]{8}$/.test(v)) fail('a color is not #RRGGBBAA')
  const n = (i: number) => Number.parseInt((v as string).slice(i, i + 2), 16)
  return { r: n(1), g: n(3), b: n(5), a: Math.round((n(7) / 255) * 1000) / 1000 }
}

function rect(v: unknown): [number, number, number, number] {
  if (!Array.isArray(v) || v.length !== 4) fail('a rect is not [x, y, width, height]')
  const [x, y, w, h] = v as unknown[]
  return [num(x, 'x'), num(y, 'y'), num(w, 'width', 0), num(h, 'height', 0)]
}

function pair(v: unknown, what: string): [number, number] {
  if (!Array.isArray(v) || v.length !== 2) fail(`${what} is not [x, y]`)
  const [x, y] = v as unknown[]
  return [num(x, what), num(y, what)]
}

function style(v: unknown): Style {
  if (!isObject(v)) return fail('a style is not an object')
  const s: Style = {}
  const background = optional(v, 'background', color)
  const borderColor = optional(v, 'border_color', color)
  const borderWidth = optional(v, 'border_width', (x) => num(x, 'border_width', 0))
  const cornerRadius = optional(v, 'corner_radius', (x) => num(x, 'corner_radius', 0))
  if (background) s.background = background
  if (borderColor) s.borderColor = borderColor
  if (borderWidth !== undefined) s.borderWidth = borderWidth
  if (cornerRadius !== undefined) s.cornerRadius = cornerRadius
  return s
}

function text(v: unknown): Text {
  if (!isObject(v)) return fail('a text is not an object')
  if (typeof v.value !== 'string') fail('a text value is not a string')
  if (typeof v.masked !== 'boolean') fail('a text has no masked flag')
  const t: Text = { value: v.value as string, masked: v.masked as boolean }
  const size = optional(v, 'size', (x) => num(x, 'size', 0))
  const weight = optional(v, 'weight', (x) =>
    Number.isInteger(x) ? num(x, 'weight', 100, 900) : fail('weight is not an integer'),
  )
  const c = optional(v, 'color', color)
  const align = optional(v, 'align', (x) =>
    oneOf(x, ['start', 'center', 'end', 'justified'] as const, 'align'),
  )
  const lines = optional(v, 'lines', (x) => (isIndex(x) ? x : fail('lines is not a count')))
  if (size !== undefined) t.size = size
  if (weight !== undefined) t.weight = weight
  if (c) t.color = c
  if (align) t.align = align
  if (lines !== undefined) t.lines = lines
  return t
}

function state(v: unknown): State {
  if (!isObject(v)) return fail('a state is not an object')
  const s: State = {}
  for (const key of ['selected', 'enabled', 'on'] as const) {
    const b = optional(v, key, bool(key))
    if (b !== undefined) s[key] = b
  }
  const value = optional(v, 'value', (x) => num(x, 'value', 0, 1))
  if (value !== undefined) s.value = value
  return s
}

/** The fields a node or an update may carry, read from `v`. */
function fields(v: Record<string, unknown>): Omit<NodeUpdate, 'id'> {
  const out: Omit<NodeUpdate, 'id'> = {}
  const parent = optional(v, 'parent', (x) => (isIndex(x) ? x : fail('parent is not an id')))
  const kind = optional(v, 'kind', (x) => oneOf(x, KINDS, 'kind'))
  const r = optional(v, 'rect', rect)
  const scroll = optional(v, 'scroll', (x) => pair(x, 'scroll'))
  const clip = optional(v, 'clip', bool('clip'))
  const alpha = optional(v, 'alpha', (x) => num(x, 'alpha', 0, 1))
  const s = optional(v, 'style', style)
  const t = optional(v, 'text', text)
  const st = optional(v, 'state', state)
  const hiddenReason = optional(v, 'hidden_reason', (x) =>
    oneOf(x, ['setting', 'unreadable', 'limit'] as const, 'hidden_reason'),
  )
  if (parent !== undefined) out.parent = parent
  if (kind) out.kind = kind
  if (r) out.rect = r
  if (scroll) out.scroll = scroll
  if (clip !== undefined) out.clip = clip
  if (alpha !== undefined) out.alpha = alpha
  if (s) out.style = s
  if (t) out.text = t
  if (st) out.state = st
  if (hiddenReason) out.hiddenReason = hiddenReason
  return out
}

function node(v: unknown): Node {
  if (!isObject(v)) return fail('a node is not an object')
  if (!isIndex(v.id)) fail('a node id is not an integer')
  if (v.kind === undefined || v.rect === undefined) fail('a node needs a kind and a rect')
  return { id: v.id as number, ...(fields(v) as Omit<Node, 'id'>) }
}

function update(v: unknown): NodeUpdate {
  if (!isObject(v)) return fail('an update is not an object')
  if (!isIndex(v.id)) fail('an update id is not an integer')
  return { id: v.id as number, ...fields(v) }
}

function list<T>(v: unknown, what: string, max: number, read: (x: unknown) => T): T[] {
  if (!Array.isArray(v)) return fail(`${what} is not a list`)
  if (v.length > max) fail(`${what} has more than ${max} items`)
  return v.map(read)
}

function viewport(v: unknown): Viewport {
  if (!isObject(v)) return fail('a viewport is not an object')
  const out: Viewport = {
    width: num(v.width, 'viewport width', 0),
    height: num(v.height, 'viewport height', 0),
    scale: num(v.scale, 'viewport scale', 0),
  }
  const safe = optional(v, 'safe_area', (x) => {
    if (!Array.isArray(x) || x.length !== 4) return fail('safe_area is not four insets')
    return x.map((n) => num(n, 'safe_area')) as [number, number, number, number]
  })
  const orientation = optional(v, 'orientation', (x) =>
    oneOf(x, ['portrait', 'landscape'] as const, 'orientation'),
  )
  if (safe) out.safeArea = safe
  if (orientation) out.orientation = orientation
  return out
}

function touch(v: unknown): Touch {
  if (!isObject(v)) return fail('a touch is not an object')
  return {
    at: nanosToMs(v.t),
    x: num(v.x, 'touch x'),
    y: num(v.y, 'touch y'),
    phase: oneOf(v.phase, ['down', 'move', 'up', 'cancel'] as const, 'touch phase'),
  }
}

function frame(v: unknown): Frame {
  if (!isObject(v)) return fail('a frame is not an object')
  if (!isIndex(v.seq)) fail('a frame seq is not an integer')
  const type = oneOf(v.type, ['keyframe', 'delta'] as const, 'frame type')
  const f: Frame = { seq: v.seq as number, at: nanosToMs(v.t), type }
  if (type === 'keyframe') {
    if (v.add !== undefined || v.update !== undefined || v.remove !== undefined)
      fail('a keyframe has delta fields')
    f.viewport = viewport(v.viewport)
    f.nodes = list(v.nodes, 'nodes', LIMITS.nodes, node)
  } else {
    if (v.viewport !== undefined || v.nodes !== undefined) fail('a delta has keyframe fields')
    if (v.add !== undefined) f.add = list(v.add, 'add', LIMITS.nodes, node)
    if (v.update !== undefined) f.update = list(v.update, 'update', LIMITS.nodes, update)
    if (v.remove !== undefined)
      f.remove = list(v.remove, 'remove', LIMITS.nodes, (x) =>
        isIndex(x) ? x : fail('a removed id is not an integer'),
      )
  }
  if (v.touches !== undefined) f.touches = list(v.touches, 'touches', LIMITS.touches, touch)
  return f
}

/** One segment's frames, or why it cannot be played (spec 10.1, 10.2). */
export function parseSegment(raw: unknown, sessionId: string): Parsed {
  try {
    if (!isObject(raw)) fail('the segment is not an object')
    const seg = raw as Record<string, unknown>
    if (seg.v !== 1) fail('the segment version is not 1')
    if (seg.session_id !== sessionId) fail("the segment is another session's")
    const frames = list(seg.frames, 'frames', LIMITS.framesPerSegment, frame)
    if (frames.length === 0) fail('the segment has no frames')
    return { ok: true, frames }
  } catch (error) {
    if (error instanceof Invalid) return { ok: false, reason: error.message }
    // A structure deep or odd enough to break a check is a bad segment, not a crash.
    return { ok: false, reason: 'the segment could not be read' }
  }
}

// --- The timeline

/** One segment as the player knows it: from the session's records, and its frames if read. */
export interface SegmentSource {
  attachmentId: string
  /** Its first frame (the record's time), ms since the epoch. */
  startAt: number
  /** Its last frame, if the record said. */
  endAt: number | null
  firstSequence: number
  lastSequence: number
  /** loaded: frames parsed; missing: never arrived; invalid: did not parse; error: not read. */
  status: 'loaded' | 'missing' | 'invalid' | 'error' | 'loading'
  frames?: Frame[]
}

export type GapReason =
  | 'missing'
  | 'invalid'
  | 'error'
  | 'loading'
  | 'sequence'
  | 'no-keyframe'
  | 'limit'
  /**
   * Segments the device never sent: a session's replay keeps its first and last segments (spec
   * 10.6, D110), and a `seq` gap between two segments marks the ones in between. A cut, not a
   * still screen.
   */
  | 'cut'

export interface Gap {
  from: number
  to: number
  reason: GapReason
}

export interface Timeline {
  startAt: number
  endAt: number
  /** Every frame, in seq order, once each. */
  frames: Frame[]
  /** Per frame: the index of the keyframe its state is built from, or -1 (inside a gap). */
  base: number[]
  /** Indexes of the keyframes. */
  keyframes: number[]
  gaps: Gap[]
}

/** What a frame costs to apply, counted against LIMITS.ops. */
const cost = (f: Frame) =>
  1 +
  (f.nodes?.length ?? 0) +
  (f.add?.length ?? 0) +
  (f.update?.length ?? 0) +
  (f.remove?.length ?? 0) +
  (f.touches?.length ?? 0)

/** Puts the segments' frames in order and finds where nothing can be shown. */
export function buildTimeline(sources: SegmentSource[]): Timeline {
  const ordered = [...sources].sort(
    (a, b) => a.firstSequence - b.firstSequence || a.startAt - b.startAt,
  )
  const gaps: Gap[] = []
  // Spec 10.6: a seq gap between two segments is the replay's cut. The frames after it resume
  // from the next segment's keyframe, so no other gap is drawn there.
  const cutAfter = new Set<number>()
  ordered.forEach((s, i) => {
    const next = ordered[i + 1]
    if (!next || next.firstSequence <= s.lastSequence + 1) return
    cutAfter.add(s.lastSequence)
    const from = s.endAt ?? s.startAt
    gaps.push({ from, to: Math.max(from, next.startAt), reason: 'cut' })
  })
  const bySeq = new Map<number, Frame>()
  ordered.forEach((s, i) => {
    if (s.status === 'loaded' && s.frames) {
      for (const f of s.frames) if (!bySeq.has(f.seq)) bySeq.set(f.seq, f)
      return
    }
    const next = ordered[i + 1]
    const to = s.endAt ?? next?.startAt ?? s.startAt
    gaps.push({ from: s.startAt, to: Math.max(to, s.startAt), reason: s.status as GapReason })
  })
  const all = [...bySeq.values()].sort((a, b) => a.seq - b.seq)
  // Past the budget, the rest of the session is a gap: never more work than the limits allow.
  let ops = 0
  let kept = 0
  while (kept < all.length && kept < LIMITS.frames) {
    ops += cost(all[kept] as Frame)
    if (ops > LIMITS.ops) break
    kept++
  }
  const frames = all.slice(0, kept)
  let startAt = Number.POSITIVE_INFINITY
  let endAt = Number.NEGATIVE_INFINITY
  const span = (v: number) => {
    if (v < startAt) startAt = v
    if (v > endAt) endAt = v
  }
  for (const f of all) span(f.at)
  for (const s of sources) {
    span(s.startAt)
    span(s.endAt ?? s.startAt)
  }
  if (startAt > endAt) startAt = endAt = 0
  const cut = all[kept]
  if (cut) gaps.push({ from: cut.at, to: Math.max(endAt, cut.at), reason: 'limit' })
  // The time of the next keyframe at or after each frame (the end when there is none).
  const nextKeyframe: number[] = new Array(frames.length)
  let upcoming = frames.at(-1)?.at ?? 0
  for (let i = frames.length - 1; i >= 0; i--) {
    const f = frames[i] as Frame
    if (f.type === 'keyframe') upcoming = f.at
    nextKeyframe[i] = upcoming
  }
  const base: number[] = []
  const keyframes: number[] = []
  let current = -1
  frames.forEach((f, i) => {
    const prev = frames[i - 1]
    const broken = prev !== undefined && f.seq !== prev.seq + 1
    const cut = broken && prev !== undefined && cutAfter.has(prev.seq)
    if (cut && f.type !== 'keyframe') {
      // Across the cut but not at a keyframe: unknown until the next one.
      gaps.push({ from: f.at, to: nextKeyframe[i] as number, reason: 'sequence' })
      current = -1
    } else if (cut) {
      current = i
      keyframes.push(i)
    } else if (f.type === 'keyframe') {
      // Frames were lost before it: what was shown is stale there, then the keyframe restores.
      if (broken && prev) gaps.push({ from: prev.at, to: f.at, reason: 'sequence' })
      current = i
      keyframes.push(i)
    } else if (current === -1) {
      // Deltas before any keyframe: nothing to apply them to.
      if (i === 0) gaps.push({ from: f.at, to: nextKeyframe[i] as number, reason: 'no-keyframe' })
    } else if (broken && prev) {
      // A seq is missing: the state is unknown until the next keyframe.
      gaps.push({ from: prev.at, to: nextKeyframe[i] as number, reason: 'sequence' })
      current = -1
    }
    base.push(current)
  })
  return { startAt, endAt, frames, base, keyframes, gaps: mergeGaps(gaps) }
}

function mergeGaps(gaps: Gap[]): Gap[] {
  const sorted = [...gaps].sort((a, b) => a.from - b.from)
  const out: Gap[] = []
  for (const g of sorted) {
    const last = out.at(-1)
    if (last && g.from <= last.to && g.reason === last.reason) last.to = Math.max(last.to, g.to)
    else out.push({ ...g })
  }
  return out
}

/**
 * The gaps the scrubber draws as breaks: time with nothing to show between segments, whether the
 * device never sent it (a cut, spec 10.6) or a segment never arrived or could not be read.
 */
export const BREAKS: ReadonlySet<GapReason> = new Set(['cut', 'missing', 'invalid', 'error'])

/** A break's share of the track, before the player clamps it to pixels (Console D: 24 to 48). */
export const BREAK_SHARE = 0.06

/**
 * Where a moment sits on the scrubber's track, 0 to 1: time is proportional except that each
 * break takes `share`, so a long unrecorded middle never crowds out what was recorded. A
 * position inside a break has no moment of its own: `time` gives the break's end, the start of
 * the next recorded part.
 */
export function trackScale(timeline: Timeline, share = BREAK_SHARE) {
  const breaks = timeline.gaps.filter((g) => BREAKS.has(g.reason) && g.to > g.from)
  const total = Math.max(1, timeline.endAt - timeline.startAt)
  const breakTime = breaks.reduce((n, g) => n + (g.to - g.from), 0)
  const recorded = Math.max(1, total - breakTime)
  const breakShare = breaks.length > 0 ? Math.min(share, 0.5 / breaks.length) : 0
  const timeShare = 1 - breakShare * breaks.length
  /** Track position (0..1) of a time; inside a break, proportionally across it. */
  const at = (t: number): number => {
    const v = Math.min(timeline.endAt, Math.max(timeline.startAt, t))
    let pos = 0
    let cursor = timeline.startAt
    for (const b of breaks) {
      if (v < b.from) break
      pos += ((b.from - cursor) / recorded) * timeShare
      if (v < b.to) return pos + ((v - b.from) / (b.to - b.from)) * breakShare
      pos += breakShare
      cursor = b.to
    }
    return Math.min(1, pos + ((v - cursor) / recorded) * timeShare)
  }
  /** The time at a track position (0..1); inside a break, the next recorded part's start. */
  const time = (p: number): number => {
    const q = Math.min(1, Math.max(0, p))
    let pos = 0
    let cursor = timeline.startAt
    for (const b of breaks) {
      const before = ((b.from - cursor) / recorded) * timeShare
      if (q < pos + before) return cursor + ((q - pos) / timeShare) * recorded
      pos += before
      if (q < pos + breakShare) return b.to
      pos += breakShare
      cursor = b.to
    }
    return Math.min(timeline.endAt, cursor + ((q - pos) / timeShare) * recorded)
  }
  /** The break a time falls in, if any. */
  const inBreak = (t: number) => breaks.find((b) => t >= b.from && t < b.to)
  return { at, time, inBreak, breaks, breakShare }
}

/** The index of the last frame at or before `at`, or -1. */
export function frameAt(timeline: Timeline, at: number): number {
  let lo = 0
  let hi = timeline.frames.length - 1
  let found = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if ((timeline.frames[mid] as Frame).at <= at) {
      found = mid
      lo = mid + 1
    } else hi = mid - 1
  }
  return found
}

/** The keyframe to start from when scrubbing to `at`: at or before it, else the first after. */
export function keyframeNear(timeline: Timeline, at: number): number | undefined {
  const keys = timeline.keyframes.map((i) => (timeline.frames[i] as Frame).at)
  const before = keys.filter((k) => k <= at).at(-1)
  return before ?? keys.find((k) => k > at)
}

/** The next or previous keyframe's time from `at`. */
export function adjacentKeyframe(
  timeline: Timeline,
  at: number,
  direction: 1 | -1,
): number | undefined {
  const keys = timeline.keyframes.map((i) => (timeline.frames[i] as Frame).at)
  return direction === 1 ? keys.find((k) => k > at + 1) : keys.filter((k) => k < at - 1).at(-1)
}

// --- The state at a moment

export interface Screen {
  viewport: Viewport
  /** Drawing order: parents first, siblings in order. */
  nodes: Node[]
  /** Touches of the last half second. */
  touches: Touch[]
  /** More nodes than LIMITS.nodes: the rest are not drawn. */
  truncated: boolean
}

export type Moment =
  | { kind: 'screen'; screen: Screen }
  | { kind: 'gap'; reason: GapReason }
  | { kind: 'none' }

const TOUCH_MS = 500

/**
 * The nodes at one frame. `nodes` keeps insertion order (a Map's), which is the drawing order:
 * a removed id leaves it, a re-added one comes last, and the cap is on the live nodes.
 * `children` indexes them by parent, so a removal walks the removed subtree only.
 */
class Tree {
  nodes = new Map<number, Node>()
  children = new Map<number, Set<number>>()
  truncated = false
  /** Operations done so far, for checkpoint spacing. */
  work = 0

  clone(): Tree {
    const t = new Tree()
    t.nodes = new Map(this.nodes)
    for (const [parent, ids] of this.children) t.children.set(parent, new Set(ids))
    t.truncated = this.truncated
    return t
  }

  private link(n: Node): void {
    if (n.parent === undefined) return
    let ids = this.children.get(n.parent)
    if (!ids) {
      ids = new Set()
      this.children.set(n.parent, ids)
    }
    ids.add(n.id)
  }

  private unlink(n: Node): void {
    if (n.parent === undefined) return
    const ids = this.children.get(n.parent)
    ids?.delete(n.id)
    if (ids?.size === 0) this.children.delete(n.parent)
  }

  /** Adds a node, or replaces the one with its id (in place); past the cap, it is dropped. */
  put(n: Node): void {
    this.work++
    const old = this.nodes.get(n.id)
    if (!old && this.nodes.size >= LIMITS.nodes) {
      this.truncated = true
      return
    }
    if (old) this.unlink(old)
    this.nodes.set(n.id, n)
    this.link(n)
  }

  update(u: NodeUpdate): void {
    this.work++
    const old = this.nodes.get(u.id)
    if (!old) return
    const n = { ...old, ...u }
    this.unlink(old)
    this.nodes.set(u.id, n)
    this.link(n)
  }

  /** Removes a node and its subtree, one operation per node visited; an unknown id costs one. */
  remove(id: number): void {
    this.work++
    const root = this.nodes.get(id)
    if (!root) return
    this.unlink(root)
    const stack = [id]
    while (stack.length) {
      const at = stack.pop() as number
      if (!this.nodes.delete(at)) continue
      this.work++
      const ids = this.children.get(at)
      if (!ids) continue
      this.children.delete(at)
      for (const child of ids) stack.push(child)
    }
  }

  /** A delta: updates (reparenting included) first, so a node moved out survives a removal. */
  apply(f: Frame): void {
    for (const u of f.update ?? []) this.update(u)
    for (const id of f.remove ?? []) this.remove(id)
    for (const n of f.add ?? []) this.put(n)
  }
}

/** The incremental state of one timeline: the last frame built, checkpoints, the last screen. */
class Replayer {
  private cursor: { index: number; tree: Tree } | undefined
  /** Saved states by frame index, at least LIMITS.checkpointOps apart. */
  private checkpoints = new Map<number, Tree>()
  private memo: { index: number; screen: Omit<Screen, 'touches'> } | undefined
  /** Operations done so far for this timeline: node writes, and a node per node copied. */
  work = 0

  constructor(private readonly timeline: Timeline) {}

  /** The screen at frame `i` (whose base keyframe is `k`), built from the nearest known state. */
  screen(i: number, k: number): Omit<Screen, 'touches'> {
    if (this.memo?.index === i) return this.memo.screen
    const { frames, base } = this.timeline
    let from = k
    let tree: Tree | undefined
    const c = this.cursor
    if (c && c.index <= i && base[c.index] === k) {
      from = c.index
      tree = c.tree
    }
    for (const index of this.checkpoints.keys())
      if (index <= i && index > from && base[index] === k) {
        from = index
        tree = undefined
      }
    if (!tree) {
      const saved = this.checkpoints.get(from)
      if (saved) {
        tree = saved.clone()
        this.work += tree.nodes.size
      } else {
        tree = new Tree()
        for (const n of (frames[k] as Frame).nodes ?? []) tree.put(n)
        this.work += tree.work
      }
    }
    const before = tree.work
    let since = tree.work
    for (let j = from + 1; j <= i; j++) {
      tree.apply(frames[j] as Frame)
      if (tree.work - since >= LIMITS.checkpointOps && !this.checkpoints.has(j)) {
        this.checkpoints.set(j, tree.clone())
        this.work += tree.nodes.size
        since = tree.work
      }
    }
    this.work += tree.work - before
    this.cursor = { index: i, tree }
    const screen = {
      viewport: (frames[k] as Frame).viewport as Viewport,
      nodes: [...tree.nodes.values()],
      truncated: tree.truncated,
    }
    this.memo = { index: i, screen }
    return screen
  }
}

const replayers = new WeakMap<Timeline, Replayer>()

/** The operations done so far drawing a timeline (0 before its first moment): bounds, in tests. */
export function replayWork(timeline: Timeline): number {
  return replayers.get(timeline)?.work ?? 0
}

/** What was on screen at `at`. */
export function momentAt(timeline: Timeline, at: number): Moment {
  const gap = timeline.gaps.find((g) => at >= g.from && at < g.to)
  // Past the budget nothing is built: the frames there were dropped. Inside a cut, nothing was
  // recorded.
  if (gap?.reason === 'limit' || gap?.reason === 'cut') return { kind: 'gap', reason: gap.reason }
  const i = frameAt(timeline, at)
  if (i === -1) return gap ? { kind: 'gap', reason: gap.reason } : { kind: 'none' }
  const k = timeline.base[i] ?? -1
  if (k === -1) return { kind: 'gap', reason: gap?.reason ?? 'sequence' }
  let replayer = replayers.get(timeline)
  if (!replayer) {
    replayer = new Replayer(timeline)
    replayers.set(timeline, replayer)
  }
  const touches: Touch[] = []
  for (let j = i; j >= Math.max(0, i - LIMITS.touchFrames); j--) {
    const f = timeline.frames[j] as Frame
    if (at - f.at > TOUCH_MS) break
    for (const t of f.touches ?? []) if (t.at <= at && at - t.at <= TOUCH_MS) touches.push(t)
  }
  touches.sort((a, b) => a.at - b.at)
  return { kind: 'screen', screen: { ...replayer.screen(i, k), touches } }
}

/**
 * One full frame on its own as a screen: a live wireframe or a screenshot (spec 16.5, 16.6), drawn
 * as the player draws a keyframe. Null for anything but a well-formed keyframe.
 */
export function keyframeScreen(raw: unknown): Screen | null {
  let f: Frame
  try {
    f = frame(raw)
  } catch {
    return null
  }
  if (f.type !== 'keyframe') return null
  const timeline = buildTimeline([
    {
      attachmentId: 'live',
      startAt: f.at,
      endAt: f.at,
      firstSequence: f.seq,
      lastSequence: f.seq,
      status: 'loaded',
      frames: [f],
    },
  ])
  const m = momentAt(timeline, f.at)
  return m.kind === 'screen' ? { ...m.screen, touches: [] } : null
}

// --- Drawing: absolute boxes in the viewport's points

export interface Box {
  id: number
  kind: Kind
  x: number
  y: number
  width: number
  height: number
  /** The intersection of the clipping ancestors' boxes, if any. */
  clip?: [number, number, number, number]
  alpha: number
  style?: Style
  text?: Text
  state?: State
  hiddenReason?: Node['hiddenReason']
}

/**
 * Every node at its place on screen: its parent's position plus its rect minus the parent's
 * scroll (spec 10.1). A node whose parent is gone is placed as a root; a parent chain longer
 * than LIMITS.depth, or a cycle, is cut there.
 */
export function boxes(screen: Screen): Box[] {
  const byId = new Map(screen.nodes.map((n) => [n.id, n]))
  const placed = new Map<
    number,
    { x: number; y: number; clip?: [number, number, number, number]; alpha: number }
  >()
  const place = (
    n: Node,
    depth: number,
    seen: Set<number>,
  ): { x: number; y: number; clip?: [number, number, number, number]; alpha: number } => {
    const done = placed.get(n.id)
    if (done) return done
    const parent = n.parent !== undefined ? byId.get(n.parent) : undefined
    let origin = { x: 0, y: 0, alpha: 1 } as {
      x: number
      y: number
      clip?: [number, number, number, number]
      alpha: number
    }
    if (parent && depth < LIMITS.depth && !seen.has(parent.id)) {
      seen.add(n.id)
      const p = place(parent, depth + 1, seen)
      const [sx, sy] = parent.scroll ?? [0, 0]
      const parentClip: [number, number, number, number] | undefined = parent.clip
        ? intersect(p.clip, [p.x, p.y, parent.rect[2], parent.rect[3]])
        : p.clip
      origin = {
        x: p.x - sx,
        y: p.y - sy,
        alpha: p.alpha,
        ...(parentClip ? { clip: parentClip } : {}),
      }
    }
    const at = {
      x: origin.x + n.rect[0],
      y: origin.y + n.rect[1],
      alpha: origin.alpha * (n.alpha ?? 1),
      ...(origin.clip ? { clip: origin.clip } : {}),
    }
    placed.set(n.id, at)
    return at
  }
  return screen.nodes.map((n) => {
    const at = place(n, 0, new Set())
    return {
      id: n.id,
      kind: n.kind,
      x: at.x,
      y: at.y,
      width: n.rect[2],
      height: n.rect[3],
      alpha: at.alpha,
      ...(at.clip ? { clip: at.clip } : {}),
      ...(n.style ? { style: n.style } : {}),
      ...(n.text ? { text: { ...n.text, value: n.text.value.slice(0, LIMITS.textChars) } } : {}),
      ...(n.state ? { state: n.state } : {}),
      ...(n.hiddenReason ? { hiddenReason: n.hiddenReason } : {}),
    }
  })
}

function intersect(
  a: [number, number, number, number] | undefined,
  b: [number, number, number, number],
): [number, number, number, number] {
  if (!a) return b
  const x = Math.max(a[0], b[0])
  const y = Math.max(a[1], b[1])
  const r = Math.min(a[0] + a[2], b[0] + b[2])
  const bottom = Math.min(a[1] + a[3], b[1] + b[3])
  return [x, y, Math.max(0, r - x), Math.max(0, bottom - y)]
}

/** A color for SVG, built from numbers only. */
export const rgba = (c: Rgba) => `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`

/**
 * Masked text as the shapes of its words: runs of characters between spaces, per line, so the
 * player draws blocks where text was and never a character.
 */
export function maskedRuns(value: string): { line: number; start: number; length: number }[] {
  const out: { line: number; start: number; length: number }[] = []
  value.split('\n').forEach((line, i) => {
    let start = -1
    const chars = [...line]
    chars.forEach((ch, j) => {
      const space = /\s/u.test(ch)
      if (!space && start === -1) start = j
      if ((space || j === chars.length - 1) && start !== -1) {
        out.push({ line: i, start, length: (space ? j : j + 1) - start })
        start = -1
      }
    })
  })
  return out
}
